/**
 * Reusable skill content component that handles both create and edit modes
 * Can be used in both page and pane contexts
 */

import { useCreateSkill, usePutSkill, useSkillById } from "@/api/skills";
import { SkillCreateForm } from "@/components/SkillCreateForm/SkillCreateForm";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import * as analytics from "@/utils/analytics";
import { route } from "@/utils/route";
import { MetadataIcon, Skill } from "@cohesive/api-client";
import React from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

function isValidMetadataIcon(icon: unknown): icon is MetadataIcon {
  if (!icon || typeof icon !== "object") return false;
  const t = (icon as { type?: string }).type;
  return t === "emoji" || t === "lucide" || t === "custom";
}

export type SkillDetailContentMode = "create" | "edit";
export type SkillDetailContentContext = "page" | "pane";

interface SkillDetailContentProps {
  mode: SkillDetailContentMode;
  skillId?: string;
  context: SkillDetailContentContext;
  onSuccess?: (skill: Skill) => void;
}

export const SkillDetailContent: React.FC<SkillDetailContentProps> = ({
  mode,
  skillId,
  context,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const appNavigate = useAppNavigate();

  // Fetch skill data for edit mode
  const { data: skill, isLoading: isSkillLoading } = useSkillById(
    skillId || "",
    {
      enabled: mode === "edit" && !!skillId,
    },
  );

  const { mutateAsync: createSkillAsync, error: createSkillError } =
    useCreateSkill();

  // Update skill mutation
  const { mutateAsync: updateSkillAsync, error: updateSkillError } =
    usePutSkill({
      onSuccess: (response) => {
        analytics.trackSkillCreated({ skill: response });
        toast.success("Skill updated");

        if (onSuccess) {
          onSuccess(response);
        } else if (context === "pane") {
          appNavigate({
            pane: "rightPane",
            to: route(ROUTES.SKILLS.SHOW, { id: response.id }),
          });
        } else {
          navigate(route(ROUTES.SKILLS.SHOW, { id: response.id }));
        }
      },
    });

  const handleSave = async (data: {
    name: string;
    description?: string;
    icon?: unknown;
    instructions?: string;
  }) => {
    if (mode === "create") {
      // The server composes the SKILL.md from these fields and creates the
      // skill and its file together — every skill has a SKILL.md from birth.
      const skill = await createSkillAsync({
        name: data.name,
        description: data.description,
        instructions: data.instructions?.trim() || undefined,
        icon: isValidMetadataIcon(data.icon) ? data.icon : undefined,
      });
      analytics.trackSkillCreated({ skill });
      if (onSuccess) {
        onSuccess(skill);
      } else if (context === "pane") {
        appNavigate({
          pane: "rightPane",
          to: route(ROUTES.SKILLS.SHOW, { id: skill.id }),
        });
      } else {
        navigate(route(ROUTES.SKILLS.SHOW, { id: skill.id }));
      }
      return;
    }
    if (mode === "edit" && skillId && skill) {
      // The PUT keeps SKILL.md's frontmatter in sync server-side.
      await updateSkillAsync({
        id: skillId,
        data: {
          name: data.name,
          description: data.description,
          icon: isValidMetadataIcon(data.icon) ? data.icon : undefined,
        },
      });
    }
  };

  if (mode === "edit" && isSkillLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">Loading skill...</div>
      </div>
    );
  }

  if (mode === "edit" && !skill && !isSkillLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">Skill not found</div>
      </div>
    );
  }

  const error = mode === "create" ? createSkillError : updateSkillError;

  return (
    <SkillCreateForm
      mode={mode}
      initialSkill={skill}
      error={error?.message}
      onSave={handleSave}
    />
  );
};
